import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import ContainerSkeleton from '../skeleton/ContainerSkeleton';
import { selectVariableData, selectLocalVariableData } from '../../store/page/selectors';
import merge from 'lodash.merge';
import { history } from '../../configureStore';
import moment from 'moment';
import { combineClassNames, replacePlaceholdersWithVariablesV2, handleOpenUrl, 
  getSizesBasedOnScreen, executeFunction, parseIfJSON, getNestedValue, isClickInsidePortal
 } from '../../utils/base';
import IconSvg from '../../../assets/icons/x-close.svg';
import { useDispatch, useSelector } from 'react-redux';
import { getVariableValue, setVariableValue } from '../../utils/globalVariableHelper';
import { SelectComponent, MultiSelectComponent, StyleUpdateChunk, RequestUpdateChunk, FunctionUpdateChunk, Breakpoints, ActionChunk } from '../../types/base';
import { submitFormRequest, mergeStyleRequest, pageRequest, formValuesRequest, updateVariableRequest, actionRequest } from '../../store/page/actions';
import generateComponentStyles from '../../utils/styleHelper';

// import CheckIcon from '../../../assets/icons/components/check.svg';
import ChevronDown from '../../../assets/icons/components/chevron-down.svg';

import * as styles from './Select.module.scss';

type PositionType = {
  top: number;
  left: number;
  width: number;
  height: number;
}

const SelectWrapper = styled.div`
  z-index: 1;
  position: relative;
  display: grid;
  ${(props) => generateComponentStyles(props.$shape)}
`;

const SelectButton = styled.button<{ isOpen: boolean }>`
  display: grid;
  cursor: pointer;
  width: 100%;
  overflow: hidden;
  z-index: 0;
  padding: 0;
  background-color: transparent;
  ${(props) => generateComponentStyles(props.$shape, props.$baseScreenResolution, props.$skipStates)}
  display: flex;
  position: relative;
  justify-content: space-between;
  align-items: center;
  gap: 8px;

  &:focus, &:focus-within {
    outline: none;
    box-shadow: ${(props) => (props.isOpen ? 'none' : 'none')};
  }
`;

const SelectInput = styled.input`
    border: 0;
    margin: 0;
    padding: 0;
    outline: none;
    width: 100%;
    min-width: 80px;
    background: transparent;
    ${(props) => generateComponentStyles(props.$shape)}
`;

const SelectOptions = styled.div`
  position: fixed;
  top: ${(props) => `${(props.top + props.height + 4) || 0}px`};
  left: ${(props) => `${props.left || 0}px`};
  width: ${(props) => `${props.width || 0}px`};
  z-index: ${(props) => `${props.zIndex || 999999999999}`};
  max-height: 245px;
  background: #fff;
  box-shadow: 0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03);
  ${(props) => generateComponentStyles(props.$shape)}

    overflow-y: auto !important;

  /* Custom Scrollbar for Webkit browsers */
  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-thumb {
    background: var(--Colors-Background-bg-quaternary, #EAECF0);
    border-radius: var(--radius-full, 9999px);
  }

  ::-webkit-scrollbar-track {
    background: transparent;
  }

  /* Custom Scrollbar for Firefox */
  scrollbar-width: thin;
  scrollbar-color: var(--Colors-Background-bg-quaternary, #EAECF0) transparent;
`;

const SelectOption = styled.div<{ isSelected: boolean }>`
  padding: 10px;
  cursor: pointer;
  background: ${(props) => (props.isSelected ? '#F9FAFB' : undefined)};
  display: flex;
  justify-content: space-between;
  align-items: center;
  overflow: hidden;

  &:hover {
    background: #F9FAFB;
  }
`;

interface SelectComponentProps extends SelectComponent {
  dataLake?: any;
  nestedTable?: boolean;
  parentIndex?: number;
  styleChunks?: any;
  parentRef?: any;
  parentStyle?: any;
  parentLoading?: boolean;
  enableLocalShimmer?: boolean;
  valueOriginal?: string;
  cache?: boolean;
}

interface MultiSelectComponentProps extends MultiSelectComponent {
  dataLake?: any;
  nestedTable?: boolean;
  parentIndex?: number;
  styleChunks?: any;
  parentRef?: any;
  parentStyle?: any;
  parentLoading?: boolean;
  enableLocalShimmer?: boolean;
  valueOriginal?: string;
  cache?: boolean;
}

const CombinedSelect: React.FC<SelectComponentProps & { pageSettings: any } | MultiSelectComponentProps & { pageSettings: any }> = ({
  id, type, options: optionsDraft, style: stDraft, value: valueDraft, events: eventsDraft, soc, placeholder, settings,
  pageSettings, dataLake, nestedTable, parentIndex, styleChunks, parentRef, parentStyle,
  skeleton: renderSkeleton2, activeState: activeStateDraft,
  parentLoading, enableLocalShimmer,
  valueOriginal, // Added valueOriginal prop
  cache,
}) => {
  const { ref } = settings || {};
  const itemRef = useRef<HTMLDivElement>(null);
  const launchExecutedRef = useRef<undefined | null | string>(null);
  const { delay = 500 } = settings || {};
  const { style: styleDraft, events, options: optionsTransformed, value, activeState, ref: realRef } = dataLake?.data ? replacePlaceholdersWithVariablesV2({ ref, style: stDraft, events: eventsDraft, value: valueDraft, options: optionsDraft, activeState: activeStateDraft }, dataLake.data) : { ref: undefined, style: stDraft, events: eventsDraft, value: valueDraft, options: optionsDraft, activeState: activeStateDraft };
const mergeStylesWithChunks = useCallback((style: any, chunks: any, index: number) => {
  const { indexes, refs } = chunks || {};
  let mergedStyle = style;
  
  // Apply index-based styles first
  if (indexes && indexes[id] && indexes[id][index] && typeof index === 'number') {
    mergedStyle = merge({}, mergedStyle, indexes[id][index]);
  }
  
  // Apply ref-based updates at the end
  if (refs && realRef) {
    const refUpdate = refs[realRef];
    if (refUpdate?.type && refUpdate.type === 'select') {
      mergedStyle = merge({}, mergedStyle, refUpdate?.update);
    }
  }
  
  return mergedStyle;
}, [id, realRef]);

  const renderSceleton = useMemo(() => {
    if (enableLocalShimmer) {
      if (cache === false) {
        return false;
      }
      return (activeState === 'loading' || parentLoading) ? (cache || (typeof valueOriginal === 'string' && /{{.*}}/.test(valueOriginal))) ? true : false : false;
    }
    return renderSkeleton2;
  }, [renderSkeleton2, activeState, enableLocalShimmer, valueOriginal, parentLoading]);

  const filteredOptionsPrevLengthRef = useRef<null | number>(null);
  const variableData = useSelector(selectVariableData); 
  const localVariableData = useSelector(selectLocalVariableData);
  // First useMemo to merge active state styles with base styles
  const mergedStyleWithState = useMemo(() => {
    let mergedStyle = styleDraft;
    if (activeState && styleDraft?.states?.[activeState]) {
      mergedStyle = merge({}, styleDraft, styleDraft.states[activeState]);
    }

    // Handle states in breakpoints
    if (styleDraft?.breakpoints) {
      mergedStyle.breakpoints = Object.entries(styleDraft.breakpoints).reduce((acc, [breakpoint, breakpointStyle]) => {
        const typedBreakpointStyle = breakpointStyle as Breakpoints;
        if (typedBreakpointStyle?.states?.[activeState]) {
          acc[breakpoint] = merge({}, typedBreakpointStyle, typedBreakpointStyle.states[activeState]);
        } else {
          acc[breakpoint] = typedBreakpointStyle;
        }
        return acc;
      }, {} as typeof styleDraft.breakpoints);
    }

    return mergedStyle;
  }, [styleDraft, activeState]);

  // Second useMemo to merge styles with chunks based on parentIndex
  const style = useMemo(() => {
    return mergeStylesWithChunks(mergedStyleWithState, styleChunks, parentIndex);
  }, [mergedStyleWithState, styleChunks, parentIndex]);

  const baseScreenResolution =  pageSettings?.baseScreenResolution ? getSizesBasedOnScreen(pageSettings?.baseScreenResolution) : undefined;
  const { grid: gridStyle, border, font, placeholder: placeholderStyle, ...otherStyle } = style || {};
  const { width, height, flex: flexStyle } = style || {};
  const { grow, shrink, basis } = flexStyle || {};
  const selectRef = useRef(null);
  const dropdownRef = useRef(null);

  const options = useMemo(() => {
    // If it's a string, try to parse it as JSON, otherwise use as is
    const parsed = typeof optionsTransformed === 'string' 
      ? parseIfJSON(optionsTransformed) 
      : optionsTransformed;
    
    // Always return an array
    return Array.isArray(parsed) ? parsed : [];
  }, [optionsTransformed]);
  const [position, setPosition] = useState<PositionType>({ top: 0, left: 0, width: 0, height: 0 });

  const isSearchEnabled = settings?.search;
  const isMultiple = type === 'multiple';
  const dispatch = useDispatch();

  const internalFns = {
    $setVariable: (key: string, value: any) => {
      setVariableValue(key, value, (localKey, localValue) => {
        dispatch(updateVariableRequest({ variable: localKey, value: localValue }));
      });
    },
    $getVariable: (key: string) => {
      return getVariableValue(key, variableData, localVariableData);
    },
    $moment: (...args: any[]) => moment(...args)
  };

  const handleActionChunk = (actionUpdates: ActionChunk[], filterLoadingState?: StyleUpdateChunk[]) => {
    actionUpdates.forEach((update) => {
      dispatch(actionRequest({ 
        actionUpdate: update, 
        componentId: id 
      }, filterLoadingState));
    });
  };

  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string | undefined>();
  const [internalValue, setInternalValue] = useState<string[]>(Array.isArray(value) ? value : value ? [value] : []);

  const {
    on_change: changeStyles,
    on_launch: launchUpdates,
    on_hover: hoverUpdates,
    on_unhover: unhoverUpdates,
    on_focus: focusUpdates,
    on_blur: blurUpdates,
    on_outclick: outclickUpdates,
  } = events || {};

  useEffect(() => {
    if ((Array.isArray(value) && value.length) || (value && typeof value === 'string')) {
      if (typeof value === 'string' && value.startsWith('[') && value.endsWith(']')) {
        setInternalValue(parseIfJSON(value));
      } else {
        setInternalValue(Array.isArray(value) ? value : value ? [value] : []);
      }
    }
  }, [value]);

  useEffect(() => {
    if (Array.isArray(value) && value.length && value.join(' ') !== internalValue?.join(' ')) {
      setInternalValue(value);
    } else if (!Array.isArray(value) && value !== internalValue[0]) {
      setInternalValue(value ? [value] : []);
    }
  }, [value]);

  const handleHover = (e: React.MouseEvent<HTMLDivElement>) => {
    if (hoverUpdates) {
      const styleUpdates = hoverUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = hoverUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = hoverUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = hoverUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      if (functionUpdates.length) {
        functionUpdates.forEach((update) => {
          if (update?.function) {
            executeFunction(update.function, e, internalFns);
          }
        });
      }
      if (styleUpdates.length) {
        dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
      }
      if (actionUpdates.length && (activeState !== 'disabled' && activeState !== 'loading')) {
        handleActionChunk(actionUpdates);
      }
      if (requestUpdates.length && (activeState !== 'disabled' && activeState !== 'loading')) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const { delay } = config || {};
          if (update?.request && method === 'action') {
            dispatch(submitFormRequest({ value, silent, delay, componentId: id }));
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }
    }
  }

  const handleUnhover = (e: React.MouseEvent<HTMLDivElement>) => {
    if (unhoverUpdates) {
      const styleUpdates = unhoverUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = unhoverUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = unhoverUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = unhoverUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      if (functionUpdates.length) {
        functionUpdates.forEach((update) => {
          if (update?.function) {
            executeFunction(update.function, e, internalFns);
          }
        });
      }
      if (styleUpdates.length) {
        dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
      }
      if (actionUpdates.length && (activeState !== 'disabled' && activeState !== 'loading')) {
        handleActionChunk(actionUpdates);
      }
      if (requestUpdates.length && (activeState !== 'disabled' && activeState !== 'loading')) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const { delay } = config || {};
          if (update?.request && method === 'action') {
            dispatch(submitFormRequest({ value, silent, delay, componentId: id }));
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }
    }
  }

  useEffect(() => {
    let clickInsidePortal = false;

    const handleMouseDown = (event) => {
      if (dropdownRef.current && dropdownRef.current.contains(event.target)) {
        clickInsidePortal = true;
      }
    };

    const handleClickOutside = (event) => {
      if (!clickInsidePortal && selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
      clickInsidePortal = false; // Reset the flag
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleMouseDown);
      document.addEventListener('mouseup', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleMouseDown);
        document.removeEventListener('mouseup', handleClickOutside);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && focusUpdates) {
      const styleUpdates = focusUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = focusUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = focusUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = focusUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      if (functionUpdates.length) {
        functionUpdates.forEach((update) => {
          if (update?.function) {
            executeFunction(update.function, undefined, internalFns);
          }
        });
      }
      if (styleUpdates.length) {
        dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
      }
      if (actionUpdates.length) {
        handleActionChunk(actionUpdates);
      }
      if (requestUpdates.length) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const { delay } = config || {};
          if (update?.request && method === 'action') {
            dispatch(submitFormRequest({ value, silent, delay, componentId: id }));
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }
    } else if (!isOpen && blurUpdates) {
      const styleUpdates = blurUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = blurUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = blurUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = blurUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      if (functionUpdates.length) {
        functionUpdates.forEach((update) => {
          if (update?.function) {
            executeFunction(update.function, undefined, internalFns);
          }
        });
      }
      if (styleUpdates.length) {
        dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
      }
      if (actionUpdates.length) {
        handleActionChunk(actionUpdates);
      }
      if (requestUpdates.length) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const { delay } = config || {};
          if (update?.request && method === 'action') {
            dispatch(submitFormRequest({ value, silent, delay, componentId: id }));
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (launchExecutedRef.current !== style?.visibility && launchUpdates && launchUpdates.length) {
      launchExecutedRef.current = style?.visibility; // Set the flag to true
      const styleUpdates = launchUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = launchUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = launchUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = launchUpdates.filter((update): update is ActionChunk => update && 'action' in update);

      if (functionUpdates.length) {
        functionUpdates.forEach((update) => {
          if (update?.function) {
            executeFunction(update.function, undefined, internalFns);
          }
        });
      }
      if (styleUpdates.length) {
        dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
      }
      if (actionUpdates.length) {
        handleActionChunk(actionUpdates);
      }
      if (requestUpdates.length) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const { delay } = config || {};
          if (update?.request && method === 'action') {
            // const newTimeout = setTimeout(() => {
            //   dispatch(submitFormRequest({ value, silent, delay, componentId: id }));
            // }, config?.delay * 1000 || 0);

            // timeoutsRef.current.push(newTimeout);
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }
    }

    return () => {
      timeoutsRef?.current?.forEach((timeout) => {
        clearTimeout(timeout);
      });
    };
  }, [launchUpdates, style?.visibility]);

  const handleChange = (e: React.MouseEvent<HTMLDivElement>, selected: string) => {
    if (type === 'multiple') {
      const newValuesBuffer = [...internalValue, selected];
      const uniqueValues = newValuesBuffer.filter((value, index) => newValuesBuffer.indexOf(value) === index);
      setInternalValue(uniqueValues);
      setSearchTerm('');
      dispatch(formValuesRequest({ field: { id: realRef || id, value: uniqueValues } }));
    }
    // If single, just replace
    else {
      setInternalValue([selected]);
      setSearchTerm('');

      dispatch(formValuesRequest({ field: { id: realRef || id, value: selected } }));
    }
    if (changeStyles) {
      const styleUpdates = changeStyles.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = changeStyles.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = changeStyles.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = changeStyles.filter((update): update is ActionChunk => update && 'action' in update);
      if (functionUpdates.length) {
        functionUpdates.forEach((update) => {
          if (update?.function) {
            executeFunction(update.function, e, internalFns);
          }
        });
      }
      if (styleUpdates.length) {
        dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
      }
      if (actionUpdates.length) {
        const filterLoadingState = styleUpdates?.filter((update) => update?.active_state === 'loading');
        handleActionChunk(actionUpdates, filterLoadingState);
      }
      if (requestUpdates.length) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const filterLoadingState = styleUpdates?.filter((update) => update?.active_state === 'loading');
          const { delay } = config || {};
          if (update?.request && method === 'command') {
            dispatch(pageRequest({ 
              location: { pathname: value }, 
              silent,
              componentId: id, 
            }, filterLoadingState));
          } else if (update?.request && method === 'action') {
            dispatch(submitFormRequest({ value, silent, delay, componentId: id }, filterLoadingState));
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }
    }
    if (soc) {
      dispatch(submitFormRequest({ value: 'soc', delay, componentId: id }));
    }
  };

  // Memoize the filtered updates to avoid recalculating on every click
  const filteredUpdates = useMemo(() => {
    if (!outclickUpdates) return null;
    
    return {
      styleUpdates: outclickUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update),
      requestUpdates: outclickUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update),
      functionUpdates: outclickUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update),
      actionUpdates: outclickUpdates.filter((update): update is ActionChunk => update && 'action' in update)
    };
  }, [outclickUpdates]);

  useEffect(() => {
    if (!outclickUpdates?.length) return undefined;
    
    const handleUnclick = (e: MouseEvent) => {
      if (activeState === 'disabled' || activeState === 'loading') return;
      
      if (
        outclickUpdates?.length &&
        selectRef.current && 
        !selectRef.current.contains(e.target as Node) &&
        !isClickInsidePortal(e.target as Node) &&
        filteredUpdates
      ) {
        // Execute function updates
        if (filteredUpdates.functionUpdates.length) {
          filteredUpdates.functionUpdates.forEach((update) => {
            if (update?.function) {
              executeFunction(update.function, undefined, internalFns);
            }
          });
        }
        
        // Apply style updates
        if (filteredUpdates.styleUpdates.length) {
          dispatch(mergeStyleRequest(filteredUpdates.styleUpdates, parentIndex));
        }
        
        // Handle action updates
        if (filteredUpdates.actionUpdates.length) {
          handleActionChunk(filteredUpdates.actionUpdates);
        }
        
        // Process request updates
        if (filteredUpdates.requestUpdates.length) {
          filteredUpdates.requestUpdates.forEach((update) => {
            const { method, value, silent, config } = update?.request || {};
            const { delay } = config || {};
            if (update?.request && method === 'action') {
              dispatch(submitFormRequest({ 
                value, 
                silent, 
                delay, 
                componentId: id 
              }));
            } else if (update?.request && method === 'open-url') {
              handleOpenUrl(value, delay, silent, history);
            }
          });
        }
      }
    };
    
    document.addEventListener('mousedown', handleUnclick);
    return () => {
      document.removeEventListener('mousedown', handleUnclick);
    };
  }, [
    parentIndex, 
    activeState, 
    id, 
    stDraft?.states?.loading,
    outclickUpdates?.length,
  ]);

  const handleSelect = (e: React.MouseEvent<HTMLDivElement>, id: string) => {
    handleChange(e, id);
    setIsOpen(false);
  };

  const handleDeleteChip = (chip: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    setInternalValue(prevValues => prevValues.filter(value => value !== chip));
  }

  const filteredOptions = (() => {
    let parsedOptions = [];
  
    if (Array.isArray(options)) {
      parsedOptions = options;
    } else if (typeof options === 'string') {
      try {
        const parsed = JSON.parse(options);
        if (Array.isArray(parsed)) {
          parsedOptions = parsed;
        }
      } catch (e) {
        // If parsing fails, fallback to an empty array
        parsedOptions = [];
      }
    }
  
    return parsedOptions.filter(option => {
      if (!isSearchEnabled || !searchTerm) {
        return true;
      }
      if (Array.isArray(option.value)) {
        return option.value.some(val => val.toLowerCase().includes(searchTerm.toLowerCase()));
      }
      if (typeof option.value === 'string') {
        return option.value.toLowerCase().includes(searchTerm.toLowerCase());
      }
      return false;
    });
  })();

  const filteredOptionsRef = useRef(filteredOptions);

useEffect(() => {
  filteredOptionsPrevLengthRef.current = filteredOptionsRef.current?.length;
  filteredOptionsRef.current = filteredOptions;
}, [filteredOptions]);

const updatePosition = useCallback((e?: Event) => {
  if (!itemRef.current) return false;
  
  const itemHeight = itemRef.current ? itemRef.current.offsetHeight : 38;
  const itemCount = filteredOptionsRef.current?.length;
  const maxDropdownHeight = window.visualViewport.height / 3;
  const dropdownHeight = Math.min(itemHeight * itemCount, 245, maxDropdownHeight);

  const viewportHeight = window.visualViewport.height;
  const scrollY = window.scrollY;

  let rect;
  if (parentRef && parentRef.current) {
    rect = parentRef.current.getBoundingClientRect();
  } else if (selectRef.current) {
    rect = selectRef.current.getBoundingClientRect();
  }

  if (!rect) return false;

  const spaceBelow = viewportHeight - rect.bottom;
  const spaceAbove = rect.top;

  let left = rect.left;
  let top = rect.top + scrollY;

  if (dropdownHeight > spaceBelow && spaceAbove > spaceBelow) {
    top = rect.top + scrollY - dropdownHeight - itemHeight - 8;
  }

  setPosition({ top, height: rect.height, left, width: rect.width });
  return true;
}, [parentRef, filteredOptionsRef]);

const updateMaxHeight = () => {
  if (dropdownRef.current) {
    const maxDropdownHeight = Math.min(window.visualViewport.height / 3, 245);
    dropdownRef.current.style.maxHeight = `${maxDropdownHeight}px`;
  }
};

const handleFunction = () => {
  updateMaxHeight();
  updatePosition();
};

useEffect(() => {
  if (isOpen) {
    handleFunction();
    // If showOptions is not yet true, set it after position calculation
    if (!showOptions) {
      setShowOptions(true);
    }
  } else {
    setShowOptions(false);
  }
}, [isOpen, showOptions]);


useEffect(() => {
  if (filteredOptionsPrevLengthRef.current !== filteredOptions.length) {

      updatePosition();
  }
}, [filteredOptions]);

useEffect(() => {
  window.addEventListener('resize', handleFunction);
  window.addEventListener('scroll', handleFunction, true); // true to capture event in the capturing phase
 // Initial position update
    updatePosition();

  return () => {
    window.removeEventListener('resize', handleFunction);
    window.removeEventListener('scroll', handleFunction, true);
  };
}, [parentRef]);


  const SelectInputComponent = () => (
    <SelectInput
      $shape={{ font }}
      value={searchTerm}
      onChange={e => setSearchTerm(e.target.value)}
      autoFocus
    />
  );

  const getTitleFromSelectOptions = (value: string[] | string) => {
    const parsedVaue = Array.isArray(value) ? value[0] : value;
    const selectedOption = options?.find(option => option.id === parsedVaue);
    return selectedOption?.value;
  }

  const PlaceholderSpan = () => {
    return (
      <span style={generateComponentStyles({ font: { ...placeholderStyle } })}>
        {placeholder}
      </span>
    );
  };

  const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (activeState === 'disabled' || activeState === 'loading') {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (!isOpen) {
      setIsOpen(true);
      // Measure position first, then show options when ready
      requestAnimationFrame(() => {
        if (updatePosition()) {
          setShowOptions(true);
        }
      });
    } else {
      setIsOpen(false);
      setShowOptions(false);
    }
  }, [activeState, isOpen, updatePosition]);

  const ChevronDownComponent = () => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 20, minWidth: 20 }}>
      <ChevronDown />
    </div>
  );

  if (renderSceleton && style) {
    return <ContainerSkeleton 
      style={style}
      baseScreenResolution={baseScreenResolution}
      skipStates={activeState === 'disabled' || activeState === 'loading'}
    />;
  }
  return (
    <SelectWrapper
     data-id={realRef || id}
      id={`${realRef || id}${(nestedTable && typeof parentIndex === 'number' && !realRef) ? `::${parentIndex}` : ''}`}
      $shape={{ grid: gridStyle, width, height, flex: { grow, shrink, basis } }}
      className={combineClassNames(style.class)}
      data-component="select"
      ref={selectRef}
      role="combobox"
      aria-haspopup="listbox"
      aria-expanded={isOpen}
      onMouseEnter={hoverUpdates?.length ? handleHover : undefined}
      onMouseLeave={unhoverUpdates?.length ? handleUnhover : undefined}
    >
      <SelectButton
        $shape={{ ...otherStyle, border, font }}
        $baseScreenResolution={baseScreenResolution}
        $skipStates={activeState === 'disabled' || activeState === 'loading'}
        onClick={handleClick}
        aria-haspopup="true"
        isOpen={isOpen}
      >
        {isOpen && isSearchEnabled && !isMultiple && SelectInputComponent()}
        {isMultiple && isSearchEnabled && (
          <div className={styles.selectItemsContainer}>
            {Array.isArray(internalValue) && internalValue?.length ? internalValue?.map((val) => {
              const selectedOption = options?.find(option => option.id === val);
              return (
                <div key={val} className={styles.selectItem}>
                  <span className={styles.chipItem}>{selectedOption?.value}</span>
                  <div
                    className={styles.chipIcon}
                    role="button"
                    onClick={(e) => handleDeleteChip(val, e)}
                  >
                    <IconSvg />
                  </div>
                </div>
              );
            }) : null}
            {isOpen ? SelectInputComponent() : null}
            {(!isOpen && !internalValue?.length) ? PlaceholderSpan() : null}
          </div>
        )}
        {isMultiple && !isSearchEnabled && (
          <div className={styles.selectItemsContainer}>
            {(Array.isArray(internalValue) && internalValue?.length) ? internalValue?.map((val) => {
              const selectedOption = Array.isArray(options) ? options?.find(option => option.id === val) : [];
              return (
                <div key={val} className={styles.selectItem}>
                  <span className={styles.chipItem}>{selectedOption?.value}</span>
                  <div
                    className={styles.chipIcon}
                    role="button"
                    onClick={(e) => handleDeleteChip(val, e)}
                  >
                    <IconSvg />
                  </div>
                </div>
              );
            }) : PlaceholderSpan()}
          </div>
        )}
        {((!isOpen && !isMultiple) || (isOpen && !isSearchEnabled && !isMultiple)) && (
          <span style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
            {internalValue?.length ? (
              <span>{getTitleFromSelectOptions(internalValue)}</span>
            ) : PlaceholderSpan()}
          </span>
        )}
        {((!isMultiple && !isSearchEnabled) || (!internalValue?.length && !isOpen)) ? ChevronDownComponent() : <div />}
      </SelectButton>
      {isOpen && showOptions && (
        createPortal(
          <SelectOptions
            $shape={{ 
              border: parentStyle?.border || {}, 
              background: style?.background ||  parentStyle?.background || [{ type: 'color', value: '#ffffff', opacity: 1 }],
              font: style?.font || {},
              zIndex: style?.zIndex || parentStyle?.zIndex || 999999999999
            }}
            ref={dropdownRef}
            data-portal="true"
            role="listbox"
            height={position.height}
            top={position.top}
            left={position.left}
            width={position.width}
            >
            {filteredOptions.map((option, index) => (
              <SelectOption
                ref={index === 0 ? itemRef : null} // Attach ref to the first item
                key={option.id}
                $shape={{ ...otherStyle }}
                onClick={(e) => handleSelect(e, option.id)}
                aria-selected={internalValue.includes(option.id)}
                isSelected={internalValue.includes(option.id)}
                role="option"
              >
                <span style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{option.value}</span>
                {internalValue.includes(option.id) ?
                  (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: 20,
                        minWidth: 20
                      }}>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g id="check">
                          <path id="Icon" d="M16.6668 5L7.50016 14.1667L3.3335 10" stroke={border?.color || "#7F56D9"} stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                        </g>
                      </svg>
                    </div>
                  ) : null}
              </SelectOption>
            ))}
          </SelectOptions>, document.body
        )
      )}
    </SelectWrapper>
  );
};

export default CombinedSelect;
